import { useEffect, useState } from 'react'
import { Hospital, getHospitalsByOrganization } from '@/lib/api/hospitals/hospital-api'
import { useAuth, useAppDispatch, useHospitalContext } from '@/store/hooks'
import { setSelectedHospital } from '@/store/slices/hospitalContextSlice'

interface UseHospitalsResult {
    hospitals: Hospital[]
    loading: boolean
    selectedHospitalId: number | null
    setSelected: (id: number | null) => void
}

export function useHospitals(): UseHospitalsResult {
    const { user } = useAuth()
    const dispatch = useAppDispatch()
    const { selectedHospitalId } = useHospitalContext()
    const [hospitals, setHospitals] = useState<Hospital[]>([])
    const [loading, setLoading] = useState(true)

    const organizationId = (user as any)?.organizationId

    useEffect(() => {
        if (!organizationId) {
            setLoading(false)
            return
        }

        let cancelled = false
        setLoading(true)

        getHospitalsByOrganization(organizationId)
            .then(res => {
                if (cancelled) return
                const list = (res?.result || []).filter(h => h.active)
                setHospitals(list)

                const persistedExists =
                    selectedHospitalId != null &&
                    list.some(h => h.id === selectedHospitalId)

                if (persistedExists) {
                    // Mantem a escolha persistida do usuario
                    return
                }

                if (list.length === 1) {
                    // Auto-selecionar quando ha apenas 1 hospital
                    dispatch(setSelectedHospital(list[0].id))
                } else if (selectedHospitalId != null) {
                    // Persistido nao existe mais (foi removido/desativado): fallback para o primeiro
                    dispatch(setSelectedHospital(list[0]?.id ?? null))
                } else if (list.length > 0) {
                    // Nada persistido ainda: default para o primeiro
                    dispatch(setSelectedHospital(list[0].id))
                }
            })
            .catch(() => {
                if (!cancelled) setHospitals([])
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId])

    const setSelected = (id: number | null) => {
        dispatch(setSelectedHospital(id))
    }

    return { hospitals, loading, selectedHospitalId, setSelected }
}
